import { writable, get } from 'svelte/store';
import {
  sendPassword,
  login,
  updateCounterparty,
  createOrder,
  precalculateOrder,
  normalizePhone
} from '../services/gigmaApi.js';
import { persistAccountToken } from './account.js';
import {
  markOrderCreated,
  hasFreshOrderMarker,
  clearOrderMarker
} from '../utils/orderGuard.js';
import {
  recordProfileSyncFailure,
  clearProfileSyncFailure
} from '../utils/profileSync.js';
import { TICKET_QTY_MIN, TICKET_QTY_MAX } from '../sections/registration.js';

const SAVED_FIELDS_KEY = 'forum-industry.registration';
const PROMO_REF_KEY = 'forum-industry.promo-ref';

const emptyValues = { email: '', name: '', surname: '', phone: '', city: '' };
const emptyChecks = {
  offerContract: false,
  privacyPolicy: false,
  userAgreement: false,
  codeOfConduct: false,
  personalData: false,
  newsletter: false
};

function loadSaved() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(SAVED_FIELDS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveValues(values) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SAVED_FIELDS_KEY, JSON.stringify(values));
  } catch {}
}

export const values = writable({ ...emptyValues, ...loadSaved() });
export const checks = writable({ ...emptyChecks });
export const errors = writable({});
export const formError = writable('');
export const step = writable('form');
export const isSubmitting = writable(false);
export const smsCode = writable('');
export const smsError = writable('');
export const isResending = writable(false);

export const promoCode = writable('');
export const promoStatus = writable('idle'); // idle | checking | applied | invalid
export const promoInfo = writable(null); // { code, finalPrice, originalPrice, promoDiscount }
export const promoError = writable('');
export const isApplyingPromo = writable(false);

// Количество билетов (один и тот же тариф). Бэк уже поддерживает quantity
// в products[]; фронт держит значение в [TICKET_QTY_MIN..TICKET_QTY_MAX].
const TICKET_QTY_KEY = 'forum-industry.registration.qty';

function clampQty(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return TICKET_QTY_MIN;
  return Math.max(TICKET_QTY_MIN, Math.min(TICKET_QTY_MAX, n));
}

function loadTicketQty() {
  if (typeof localStorage === 'undefined') return TICKET_QTY_MIN;
  try {
    const raw = localStorage.getItem(TICKET_QTY_KEY);
    return raw == null ? TICKET_QTY_MIN : clampQty(raw);
  } catch {
    return TICKET_QTY_MIN;
  }
}

function persistTicketQty(n) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(TICKET_QTY_KEY, String(n));
  } catch {}
}

export const ticketQty = writable(loadTicketQty());

export function setTicketQty(value) {
  const n = clampQty(value);
  ticketQty.set(n);
  persistTicketQty(n);
}

// Восстановить qty из localStorage. Вызывается в onMount страницы после
// resetForm(), который сбрасывает стор в TICKET_QTY_MIN.
export function hydrateTicketQty() {
  ticketQty.set(loadTicketQty());
}

// Если в sessionStorage есть свежий маркер «заказ уже создан» (TTL 1ч) — блокируем
// повторный сабмит. Это закрывает кейс «пользователь вернулся «Назад» в браузере
// после оплаты YooKassa и снова прошёл форму».
export const orderAlreadyCreated = writable(hasFreshOrderMarker());

// Кнопка «Купить ещё один билет» в баннере — снимает маркер, чтобы форма открылась.
export function resetOrderGuard() {
  clearOrderMarker();
  orderAlreadyCreated.set(false);
  resetForm();
}

/**
 * Маска российского телефона: пока юзер вводит цифры — добавляются
 * скобки/пробелы/дефис. Принимает любой ввод (вставка из буфера, цифры
 * подряд, 7/8 в начале) и нормализует к виду «+7 (999) 123-45-67».
 *
 * Caret position не сохраняем — после форматирования курсор уходит в конец.
 * Это компромисс ради простоты (без зависимостей вроде imask/maskito).
 * Для обычного потока ввода «снизу вверх» не мешает.
 */
function formatPhone(raw) {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  // 8XXXXXXXXXX → 7XXXXXXXXXX. 10 цифр без префикса → добавляем 7 спереди.
  if (digits.startsWith('8')) digits = '7' + digits.slice(1);
  else if (!digits.startsWith('7')) digits = '7' + digits;
  digits = digits.slice(0, 11);

  const tail = digits.slice(1); // XXX XXX XX XX
  let out = '+7';
  if (tail.length > 0) out += ' (' + tail.slice(0, 3);
  if (tail.length >= 3) out += ')';
  if (tail.length > 3) out += ' ' + tail.slice(3, 6);
  if (tail.length > 6) out += '-' + tail.slice(6, 8);
  if (tail.length > 8) out += '-' + tail.slice(8, 10);
  return out;
}

export function setField(name, value) {
  const next = name === 'phone' ? formatPhone(value) : value;
  values.update((v) => ({ ...v, [name]: next }));
  errors.update((e) => {
    if (!e[name]) return e;
    const nextErrors = { ...e };
    delete nextErrors[name];
    return nextErrors;
  });
}

export function toggleCheck(name) {
  checks.update((c) => ({ ...c, [name]: !c[name] }));
}

// Серверная валидация имени/фамилии — `nullable|string|min:2|max:255`
// (UpdateRequest в itecho-erp-backend, master). Раньше фронт проверял
// только непустоту — юзер с однобуквенным именем получал 422 от бэка
// внутри updateCounterparty, ошибка молча проглатывалась, и в БД
// counterparty оставался без first_name. Здесь зеркалим серверный диапазон,
// чтобы 422 в этом месте не возникал в принципе.
const NAME_MIN = 2;
const NAME_MAX = 255;

function validate() {
  const v = get(values);
  const next = {};
  if (!v.email.trim() || !/\S+@\S+\.\S+/.test(v.email)) next.email = 'Укажите корректный email';
  const name = v.name.trim();
  if (!name) next.name = 'Укажите имя';
  else if (name.length < NAME_MIN) next.name = `Имя должно содержать минимум ${NAME_MIN} символа`;
  else if (name.length > NAME_MAX) next.name = 'Имя слишком длинное';
  const surname = v.surname.trim();
  if (!surname) next.surname = 'Укажите фамилию';
  else if (surname.length < NAME_MIN) next.surname = `Фамилия должна содержать минимум ${NAME_MIN} символа`;
  else if (surname.length > NAME_MAX) next.surname = 'Фамилия слишком длинная';
  if (!v.phone.trim()) {
    next.phone = 'Укажите телефон';
  } else {
    // normalizePhone приводит ввод к формату 7XXXXXXXXXX. Любая комбинация
    // «+7 999 123-45-67», «8 (999) 123-45-67», «79991234567», «9991234567»
    // — должна дать ровно 11 цифр, начинающихся с 7.
    const normalized = normalizePhone(v.phone);
    if (!/^7\d{10}$/.test(normalized)) {
      next.phone = 'Укажите номер в формате +7 999 123-45-67';
    }
  }
  if (!v.city.trim()) next.city = 'Укажите город';
  errors.set(next);
  return Object.keys(next).length === 0;
}

export async function submitForm() {
  // P1 — защита от двойного клика: пока submit крутится, повторный вход в функцию
  // молча игнорируется (UI и так disable'ит кнопку, но защита уровня store плотнее).
  if (get(isSubmitting)) return;

  // P0 — если в этой вкладке уже был успешный createOrder за последний час,
  // не разрешаем второй заказ. UI покажет баннер «Заказ уже оформлен».
  if (hasFreshOrderMarker()) {
    orderAlreadyCreated.set(true);
    return;
  }

  // P2 — поднимаем isSubmitting до validate(), чтобы между проверками и сетевым
  // вызовом не было щели для двойного нажатия. finally сбросит флаг в любом случае.
  isSubmitting.set(true);
  try {
    if (!validate()) return;
    const c = get(checks);
    if (!c.offerContract || !c.privacyPolicy || !c.personalData) {
      formError.set('Пожалуйста, примите обязательные соглашения');
      return;
    }
    formError.set('');
    await sendPassword(get(values).email);
    step.set('verify');
    smsCode.set('');
    smsError.set('');
  } catch (e) {
    formError.set(mapAuthSubmitError(e));
  } finally {
    isSubmitting.set(false);
  }
}

function sanitizeMessage(raw) {
  return String(raw ?? '').replace(/Bearer [\w.\-]+/g, 'Bearer [REDACTED]');
}

function mapAuthSubmitError(e) {
  if (e?.kind === 'auth') return 'Не удалось авторизоваться. Проверьте email.';
  if (e?.kind === 'server' && e?.httpStatus >= 500) return 'Сервер временно недоступен. Попробуйте позже.';
  return e?.message || 'Не удалось отправить код. Попробуйте позже.';
}

function mapVerifyError(e) {
  const msg = e?.message ?? '';
  // Email-код истёк — бэк ловит на login и шлёт строкой (исторически — без code).
  if (msg.includes('Send password before login') || msg.includes('Password TTL')) {
    return 'Срок действия кода истёк. Нажмите «Отправить повторно».';
  }
  if (e?.endpoint === 'login' && (e?.httpStatus === 400 || e?.httpStatus === 401)) {
    return 'Неверный код. Проверьте письмо и попробуйте снова.';
  }
  // Pricing-ошибки бэка (1001 — товара нет на складе и т.п.)
  if (e?.kind === 'pricing') {
    if (e?.code === 1001) return 'Товар временно недоступен. Обратитесь к организатору.';
    return e?.message || 'Заказ нельзя оформить с этим товаром.';
  }
  // Discount-ошибки бэка (2001–2099 — промокод не применим, лимит и т.п.).
  // Бэк уже даёт human-readable текст; «Вы уже использовали эту скидку» (2007) попадёт сюда.
  if (e?.kind === 'discount' && e?.message) {
    return e.message;
  }
  // 502 от createOrder — YooKassa упала, заказ откатили.
  if (e?.endpoint === 'createOrder' && e?.httpStatus === 502 && e?.message) {
    return e.message;
  }
  if (e?.httpStatus >= 500) return 'Сервер временно недоступен. Попробуйте позже.';
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}

// Маппер для catch вокруг updateCounterparty в verifyCode. Цель — короткое
// human-readable сообщение для баннера в /account; технические детали
// (status code, raw body) остаются в console.warn для отладки.
//
// Сырое e.message не используем — в bad case это `updateCounterparty: HTTP 500`
// либо технический текст от Laravel exception. Юзеру нужно понимать, что
// делать дальше (обратиться к организатору / попробовать позже).
function mapProfileSyncError(e) {
  if (e?.kind === 'auth' || e?.httpStatus === 401) {
    return 'Сессия истекла до сохранения профиля';
  }
  if (e?.httpStatus === 422) {
    // Серверная валидация отклонила one of fields. Сейчас на бэке
    // first_name/last_name требуют 2..255 символов — фронт уже зеркалит
    // это в validate(). 422 здесь — редкий corner case (например, email
    // прошёл клиентскую регулярку, но не filter_var на бэке).
    return 'Бэк отклонил данные профиля';
  }
  if (typeof e?.httpStatus === 'number' && e.httpStatus >= 500) {
    return 'Сервер временно недоступен — профиль не сохранён';
  }
  return 'Не удалось сохранить имя и фамилию в профиле';
}

function mapPromoApplyError(e) {
  if (e?.kind === 'pricing' && e?.code === 1001) {
    return 'Товар временно недоступен. Обратитесь к организатору.';
  }
  if (e?.kind === 'discount' && e?.message) {
    return e.message; // 2001–2099 — бэк уже даёт текст
  }
  if (e?.httpStatus >= 500) return 'Сервер временно недоступен. Попробуйте позже.';
  return e?.message || 'Не удалось проверить промокод. Попробуйте позже.';
}

export async function resendCode() {
  isResending.set(true);
  smsError.set('');
  try {
    await sendPassword(get(values).email);
    smsError.set('Новый код отправлен на ваш email.');
  } catch {
    smsError.set('Не удалось отправить код. Попробуйте позже.');
  } finally {
    isResending.set(false);
  }
}

export async function verifyCode(productId, onSuccess, onAfterPayment) {
  // P1 — повторный клик «Подтвердить и оплатить» молча игнорируем.
  if (get(isSubmitting)) return;

  // P0 — если уже создавали заказ в этой вкладке за последний час, не дёргаем
  // createOrder снова, чтобы не дублировать запись в Gigma.
  if (hasFreshOrderMarker()) {
    orderAlreadyCreated.set(true);
    return;
  }

  const code = get(smsCode);
  if (!code.trim()) {
    smsError.set('Введите код из письма');
    return;
  }
  isSubmitting.set(true);
  smsError.set('');
  try {
    const v = get(values);
    const accessToken = await login(v.email, code);

    // Сохраняем токен в sessionStorage до редиректа на YooKassa, чтобы при
    // возврате на /account (success_payment_url) ЛК сразу подхватил сессию
    // без повторного ввода email-кода.
    persistAccountToken(accessToken);

    saveValues(v);

    try {
      // Телефон форума пишем в phone_2 (контактный, без SMS-верификации).
      // phone_1 — основной/SMS-верифицируемый, его не трогаем. Бэк: nullable|string|max:20.
      const counterpartyPatch = {
        first_name: v.name,
        last_name: v.surname,
        email: v.email
      };
      const phone = normalizePhone(v.phone).slice(0, 20);
      if (phone) counterpartyPatch.phone_2 = phone; // пустой телефон не отправляем — иначе уйдёт ""
      await updateCounterparty(accessToken, counterpartyPatch);
      // PUT прошёл — гасим возможный старый warning от прошлой попытки
      // (юзер мог вернуться на регистрацию после неудачи).
      clearProfileSyncFailure();
    } catch (e) {
      // updateCounterparty намеренно non-blocking (см. agent/forms-and-state.md):
      // дальше идёт createOrder + редирект на YooKassa, потеря оплаты из-за
      // побочного PUT недопустима. Но раньше ошибка тонула в console.warn —
      // в БД counterparty оставался с null first_name/last_name, юзер в ЛК
      // видел «не указано» без объяснения. Теперь причину пишем в
      // sessionStorage, /account показывает неблокирующий баннер.
      console.warn('[updateCounterparty] failed (non-blocking):', sanitizeMessage(e?.message));
      recordProfileSyncFailure(mapProfileSyncError(e));
    }

    const appliedPromo = get(promoStatus) === 'applied' ? (get(promoInfo)?.code ?? null) : null;
    const { paymentLink, orderId } = await createOrder(accessToken, productId, appliedPromo, get(ticketQty));

    // P0 — фиксируем «в этой вкладке уже создали заказ». Если пользователь
    // нажмёт «Назад» в браузере после YooKassa и снова попадёт на форму —
    // покажется баннер вместо повторного оформления. orderId сохраняем
    // отдельно — AccountPage использует его как точный таргет polling'а
    // при возврате с YooKassa (без эвристики «самый свежий pending»).
    markOrderCreated({ paymentLink, orderId });
    orderAlreadyCreated.set(true);

    onSuccess?.();

    if (paymentLink) {
      window.location.href = paymentLink;
    } else {
      // Бесплатный заказ (final_price === 0 / payment_link === null от сервера):
      // в YooKassa идти незачем, токен ЛК уже в sessionStorage через
      // persistAccountToken — AccountPage подхватит сессию сразу.
      window.location.href = '/account';
    }
  } catch (e) {
    smsError.set(mapVerifyError(e));
  } finally {
    isSubmitting.set(false);
  }
}

export function resetForm() {
  step.set('form');
  smsCode.set('');
  smsError.set('');
  errors.set({});
  formError.set('');
  promoStatus.set('idle');
  promoInfo.set(null);
  promoError.set('');
  ticketQty.set(TICKET_QTY_MIN);
}

export function setPromoCode(value) {
  promoCode.set(value);
  if (get(promoStatus) !== 'idle') {
    promoStatus.set('idle');
    promoInfo.set(null);
    promoError.set('');
  }
}

export async function applyPromoCode(productId) {
  const code = get(promoCode).trim();
  if (!code) {
    promoError.set('Введите промокод');
    promoStatus.set('invalid');
    return;
  }
  if (!/^[A-Za-z0-9\-]+$/.test(code)) {
    promoError.set('Промокод может содержать только латинские буквы, цифры и дефис');
    promoStatus.set('invalid');
    return;
  }
  isApplyingPromo.set(true);
  promoStatus.set('checking');
  promoError.set('');
  try {
    const data = await precalculateOrder(productId, code, get(ticketQty));
    const err = data?.discount_error;
    const applied = data?.applied_discount;
    if (err || !applied) {
      promoError.set(err?.reason || 'Промокод не применим');
      promoStatus.set('invalid');
      promoInfo.set(null);
      return;
    }
    promoInfo.set({
      code: applied.code || code,
      name: applied.name,
      finalPrice: Number(data.final_price),
      originalPrice: Number(data.original_price ?? data.price),
      promoDiscount: Number(data.promo_discount_amount ?? data.promo_discount ?? 0)
    });
    promoStatus.set('applied');
  } catch (e) {
    promoError.set(mapPromoApplyError(e));
    promoStatus.set('invalid');
    promoInfo.set(null);
  } finally {
    isApplyingPromo.set(false);
  }
}

export function clearPromoCode() {
  promoCode.set('');
  promoStatus.set('idle');
  promoInfo.set(null);
  promoError.set('');
}

export async function hydratePromoFromRef(productId) {
  if (!productId || typeof sessionStorage === 'undefined') return;
  if (get(promoStatus) === 'applied') return;
  let code = '';
  try {
    code = sessionStorage.getItem(PROMO_REF_KEY) || '';
  } catch {
    return;
  }
  if (!code) return;
  setPromoCode(code);
  await applyPromoCode(productId);
}
